import { yupResolver } from '@hookform/resolvers/yup';
import ModalStack from '-/Custom/ModalStack';
import type { EventEditFormValues } from '-/Event/EventEdit.types';
import { useLocalSearchParams } from 'expo-router';
import { DateTime } from 'luxon';
import { FormProvider, useForm } from 'react-hook-form';
import { type Client, useClient } from 'urql';
import { graphql } from '@/gql/graphql';
import { MAXIMUM_EVENT_TEAM_COUNT } from '@/utils/constants';
import eventTypeMap from '@/utils/eventTypeMap';
import getEmptyEntity from '@/utils/getEmptyEntity';
import getRandomColor from '@/utils/getRandomColor';

const GetEventEditForm = graphql(/* GraphQL */ `
  query GetEventEditForm($eventId: uuid!, $teamLimit: Int!) {
    event_by_pk(id: $eventId) {
      id
      title
      location
      datetime
      notes
      eventType: event_type
      teamId: team_id
      homeTeamId: home_team_id
      tagGroupId: tag_group_id
      hasStandings: has_standings
      parentId: parent_id
      eventTeams: event_teams(
        limit: $teamLimit
        order_by: [{ is_home: asc }, { team: { title: asc } }]
      ) {
        event_id
        team_id
        team {
          id
          title
          avatarId: avatar_id
          color: home_primary_color
        }
      }
    }
  }
`);

type EventEditSearchParams = {
  eventId?: string;
  eventType?: NonNullable<EventEditFormValues['eventType']>;
};

function splitEventEditSearchParams({
  eventId,
  eventType,
  ...params
}: EventEditSearchParams) {
  return { eventId, eventType, params };
}

function getEventTypeValidator(eventType: EventEditFormValues['eventType']) {
  if (eventType) {
    return eventTypeMap[eventType].validator;
  }

  return eventTypeMap.game.validator;
}

async function getEventEditDefaultValues({
  client,
  eventId,
  eventType,
  params,
}: {
  client: Client;
  eventId?: string;
  eventType?: EventEditSearchParams['eventType'];
  params: Omit<EventEditSearchParams, 'eventId' | 'eventType'>;
}) {
  if (eventId) {
    const { data } = await client
      .query(
        GetEventEditForm,
        {
          eventId,
          teamLimit: MAXIMUM_EVENT_TEAM_COUNT,
        },
        {
          requestPolicy: 'network-only',
        },
      )
      .toPromise();

    return {
      ...(data?.event_by_pk ? data.event_by_pk : {}),
      teams: data?.event_by_pk?.eventTeams.map((eventTeam) => ({
        id: eventTeam.team.id,
        title: eventTeam.team.title,
        avatarId: eventTeam.team.avatarId || '',
        color: eventTeam.team.color || '',
      })),
      eventType: data?.event_by_pk?.eventType,
      title: data?.event_by_pk?.title || '',
      location: data?.event_by_pk?.location || '',
      notes: data?.event_by_pk?.notes || '',
    };
  }

  const teamCount = eventType === 'game' ? 2 : eventType === 'group' ? 4 : 0;

  return {
    teams: Array.from({ length: teamCount }, () =>
      getEmptyEntity(getRandomColor()),
    ),
    teamId: null,
    parentId: null,
    title: '',
    location: '',
    notes: '',
    hasStandings: false,
    eventType,
    datetime:
      eventType === 'game'
        ? DateTime.now().plus({ weeks: 1, hours: 1 }).startOf('hour').toISO()
        : null,
    ...params,
  };
}

export default function EventEditLayout() {
  const client = useClient();

  const { eventId, eventType, params } = splitEventEditSearchParams(
    useLocalSearchParams<EventEditSearchParams>(),
  );

  const form = useForm<EventEditFormValues>({
    resolver: (values, context, options) =>
      yupResolver(getEventTypeValidator(values.eventType))(
        values,
        context,
        options,
      ),
    defaultValues: () =>
      getEventEditDefaultValues({ client, eventId, eventType, params }),
  });

  return (
    <FormProvider {...form}>
      <ModalStack />
    </FormProvider>
  );
}
